import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/contexts/AuthContext';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ManualLeadUploadModal } from '@/components/dashboard/ManualUploadLeads/ManualLeadUploadModal';
import { InfoModalTrigger } from '@/components/ui/InfoModalTrigger';
import { Upload, PhoneOff } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { format } from 'date-fns';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import VideoLink from '@/components/ui/VideoLink';
import { useVideoBySlug } from '@/hooks/useVideos';

interface QueueItem {
  id: string;
  customer_phone: string;
  customer_name?: string | null;
  call_type: string;
  status: string;
  attempts: number;
  last_attempt_time?: string | null;
  created_at: string;
  last_error?: string | null;
}

const ITEMS_PER_PAGE = 15;

export default function CallQueuePage() {
  const { user } = useAuth();
  const UploadLeadsVideo = useVideoBySlug('upload-leads'); 
  const CallQueueOverviewVideo = useVideoBySlug('call-queue-overview');

  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0); // Page is 0-indexed
  const [totalCount, setTotalCount] = useState(0);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [phoneToBlock, setPhoneToBlock] = useState<string | null>(null);
  const [blockReason, setBlockReason] = useState<string>('customer_requested');
  const [blockNotes, setBlockNotes] = useState<string>('');

  const fetchQueue = useCallback(async (pageToFetch: number) => {
    if (!user) {
      setQueueItems([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    const from = pageToFetch * ITEMS_PER_PAGE;
    const to = from + ITEMS_PER_PAGE - 1;
    const { data, error, count } = await supabase
      .from('outbound_call_queue')
      .select('*', { count: 'exact' })
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) {
      console.error('Failed to fetch queue', error);
      toast.error(`Failed to fetch queue: ${error.message}`);
      setQueueItems([]);
      setTotalCount(0);
    } else {
      setQueueItems((data || []) as QueueItem[]);
      setTotalCount(count || 0);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchQueue(page);
  }, [user, page, fetchQueue]);

  const cancelTask = async (id: string) => {
    const { error } = await supabase
      .from('outbound_call_queue')
      .update({ status: 'skipped' })
      .eq('id', id)
      .in('status', ['pending', 'processing']);

    if (error) {
      console.error('Failed to skip task', error);
      toast.error(`Skip failed: ${error.message}`);
    } else {
      toast.success('Task skipped');
      setQueueItems(items =>
        items.map(it => (it.id === id ? { ...it, status: 'skipped' } : it))
      );
    }
  };

  const openBlockNumberModal = (phone: string) => {
    setPhoneToBlock(phone);
    setBlockReason('customer_requested'); // Reset to default when opening
    setBlockNotes(''); // Reset to default when opening
    setBlockModalOpen(true);
  };

  const reactivateTask = async (id: string) => {
    const { error } = await supabase
      .from('outbound_call_queue')
      .update({ status: 'pending' })
      .eq('id', id)
      .eq('status', 'skipped');

    if (error) {
      console.error('Failed to reactivate task', error);
      toast.error(`Reactivation failed: ${error.message}`);
    } else {
      toast.success('Task reactivated to pending');
      setQueueItems(items =>
        items.map(it => (it.id === id ? { ...it, status: 'pending' } : it))
      );
    }
  };

  const blockNumber = async (phone: string) => {
    if (!phone) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/do-not-call-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ 
          phone_number: phone,
          reason: blockReason,
          notes: blockNotes.trim() || undefined
        })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to block number');
      }
      
      toast.success('Number blocked successfully');
      setBlockReason('customer_requested'); // Reset form state
      setBlockNotes('');
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : 'An unknown error occurred';
      toast.error(errorMessage);
    } finally {
      setBlockModalOpen(false); // Close modal in finally block
    }
  };

  const statusVariant = (status: string) => {
    switch (status) {
      case 'failed':
        return 'destructive';
      case 'completed':
        return 'default';
      case 'skipped':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  const formatDate = (iso: string | null | undefined) => {
    if (!iso) return '—';
    try {
      return format(new Date(iso), 'MMM d, HH:mm');
    } catch {
      console.warn(`Invalid date format encountered: ${iso}`);
      return iso;
    }
  };

  const formatCallType = (type: string) => {
    // Special case for 'api' to display as 'API'
    if (type === 'api') {
      return 'API';
    }
    return type
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const handleRefresh = () => {
    if (page !== 0) {
      setPage(0);
    } else {
      fetchQueue(0);
    }
  };

  const callQueueInfoContent = (
    <>
      <div className="mb-4">
        <p className="mb-4 text-gray-700">
          The Call Queue shows all the call tasks your AI employee has to make or has made. 
          This page displays outbound calls from manual uploads and calendar integrations.
        </p>
        
        <div className="space-y-6">
          {CallQueueOverviewVideo && 
          <div>
            <h4 className="font-bold mb-2">Call Queue Overview</h4>
            <p className="text-sm text-gray-600 mb-3">Learn how to navigate and understand your call queue</p>
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={CallQueueOverviewVideo || "https://www.loom.com/embed/f1d3f8ad75574739a4a382c65b561fe8?sid=d8e8e80c-f118-462c-b5cc-7a953afce113"}
                frameBorder="0"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full rounded-lg"
              />
            </div>
            <VideoLink to="call-queue-overview" />
          </div>}

          {CallQueueOverviewVideo && <div>
            <h4 className="font-bold mb-2">How to Upload Leads</h4>
            <p className="text-sm text-gray-600 mb-3">Step-by-step guide to manually upload leads and start calls</p>
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={ UploadLeadsVideo || "https://www.loom.com/embed/a4a47571ef7e45ba8c7c263cb4024bfb?sid=f69227bb-1667-4f3b-b817-114d374c8d0e"}
                frameBorder="0"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full rounded-lg"
              />
            </div>
            <VideoLink to="upload-leads" />
          </div>}
        </div>

        <div className="space-y-3">
          <div>
            <h4 className="font-medium mb-1">Call Statuses:</h4>
            <ul className="text-sm space-y-1 text-gray-600">
              <li><strong>Pending:</strong> Call is queued and will be made soon</li>
              <li><strong>Processing:</strong> AI is currently making the call</li>
              <li><strong>Completed:</strong> Call was successfully completed</li>
              <li><strong>Skipped:</strong> Call was manually skipped or canceled</li>
              <li><strong>Failed:</strong> Call encountered an error</li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium mb-1">Actions Available:</h4>
            <ul className="text-sm space-y-1 text-gray-600">
              <li><strong>Cancel:</strong> Stop a pending call from being made</li>
              <li><strong>Reactivate:</strong> Put a skipped call back in the queue</li>
              <li><strong>Block Number:</strong> Add a phone number to your Do Not Call list</li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium mb-1">Call Types:</h4>
            <ul className="text-sm space-y-1 text-gray-600">
              <li><strong>Manual Upload:</strong> Leads uploaded via CSV</li>
              <li><strong>Calendar Integration:</strong> Calls from your connected calendars</li>
              <li><strong>API:</strong> Calls triggered via API integration</li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  return (
    <div className="space-y-6 p-4 md:p-6">
      <Toaster richColors position="top-right" />
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground">Call Queue</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleRefresh} disabled={loading}>Refresh</Button>
          <Button onClick={() => setUploadModalOpen(true)}>
            <Upload className="mr-2 h-4 w-4" /> Upload Leads
          </Button>
          <InfoModalTrigger modalTitle="Call Queue Guide" modalContent={callQueueInfoContent} />
        </div>
      </div>
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-slate-gray">Queued Calls</h2>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Call Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Attempts</TableHead>
                <TableHead>Last Attempt</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10 text-gray-500">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : queueItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10 text-slate-gray">
                    No queue items found.
                  </TableCell>
                </TableRow>
              ) : (
                queueItems.map(item => (
                  <TableRow key={item.id} className="hover:bg-gray-50">
                    <TableCell className="whitespace-nowrap">
                      {item.customer_name || '—'}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{item.customer_phone}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatCallType(item.call_type)}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      <Badge variant={statusVariant(item.status)}>{item.status}</Badge>
                    </TableCell>
                    <TableCell className="text-center">{item.attempts}</TableCell>
                    <TableCell>{formatDate(item.last_attempt_time)}</TableCell>
                    <TableCell>{formatDate(item.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <div className="flex-1">
                          {['pending', 'processing'].includes(item.status) && (
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={loading}
                              onClick={() => cancelTask(item.id)}
                              className="w-full max-w-[100px]"
                            >
                              Cancel
                            </Button>
                          )}
                          {item.status === 'skipped' && (
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={loading}
                              onClick={() => reactivateTask(item.id)}
                              className="w-full max-w-[100px]"
                            >
                              Reactivate
                            </Button>
                          )}
                          {item.status === 'completed' && (
                            <span className="text-sm text-green-600">✓ Done</span>
                          )}
                          {item.status === 'failed' && (
                            <span className="text-sm text-red-600">Failed</span>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50"
                          disabled={loading}
                          onClick={() => openBlockNumberModal(item.customer_phone)}
                          title="Add to Do Not Call list"
                        >
                          <PhoneOff className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        {totalCount > ITEMS_PER_PAGE && (
          <div className="flex items-center justify-between p-4 border-t border-gray-200">
            <span className="text-sm text-gray-700">Page {page + 1} of {totalPages} ({totalCount} tasks)</span>
            <div className="space-x-2">
              <Button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0 || loading} variant="outline" size="sm">Previous</Button>
              <Button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1 || loading} variant="outline" size="sm">Next</Button>
            </div>
          </div>
        )}
      </div>
      <ManualLeadUploadModal isOpen={uploadModalOpen} onOpenChange={setUploadModalOpen} />
      <AlertDialog open={blockModalOpen} onOpenChange={setBlockModalOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Block This Number?</AlertDialogTitle>
            <AlertDialogDescription>
              Add {phoneToBlock} to your Do Not Call list. They won't be called again by your AI Employee.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="block-reason">Reason for blocking</Label>
              <Select value={blockReason} onValueChange={setBlockReason}>
                <SelectTrigger id="block-reason">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer_requested">Customer requested</SelectItem>
                  <SelectItem value="wrong_number">Wrong number</SelectItem>
                  <SelectItem value="competitor">Competitor</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="block-notes">Notes (optional)</Label>
              <Textarea
                id="block-notes"
                placeholder="Add any additional context..."
                value={blockNotes}
                onChange={(e) => setBlockNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setBlockModalOpen(false);
              setBlockReason('customer_requested');
              setBlockNotes('');
            }}>
              Cancel
            </AlertDialogCancel>
            <Button 
              variant="destructive" 
              onClick={() => { 
                if (phoneToBlock) blockNumber(phoneToBlock);
              }}
            >
              Yes, Block Number
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}